import React from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Flex,
  Input,
  Spinner,
} from '@chakra-ui/react';
import { Plus, Search } from 'lucide-react';

const DragHandle = ({ ...props }) => (
  <Flex
    align="center"
    justify="center"
    cursor="grab"
    color="whiteAlpha.300"
    _hover={{ color: 'whiteAlpha.600' }}
    transition="color 0.12s ease"
    flexShrink={0}
    {...props}
  >
    <GripVerticalIcon />
  </Flex>
);

function GripVerticalIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="9" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Chronological custom / research fields for entity detail views.
 */
export default function DetailViewFieldsList({
  fields,
  dragIdx,
  dragOverIdx,
  onDragStart,
  onDragOver,
  onDragEnd,
  renamingFieldId,
  renameValue,
  renameInputRef,
  onRenameChange,
  onFinishRename,
  onCancelRename,
  onFieldContextMenu,
  renderFieldValue,
  onAddField,
  addFieldLabel = 'Add research field',
}) {
  return (
    <>
      {fields.map((field, idx) => (
        <Box
          key={field.id}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragOver={(e) => onDragOver(e, idx)}
          onDragEnd={onDragEnd}
          opacity={dragIdx === idx ? 0.4 : 1}
          borderTop={dragOverIdx === idx && dragIdx !== idx ? '2px solid' : '2px solid transparent'}
          borderColor={dragOverIdx === idx && dragIdx !== idx ? 'rgba(255,255,255,0.4)' : 'transparent'}
          transition="opacity 0.12s ease"
          role="group"
        >
          <Box h="1px" bg="whiteAlpha.50" my={4} />
          <HStack align="flex-start" spacing={0}>
            <DragHandle opacity={0} _groupHover={{ opacity: 0.6 }} mt="2px" ml={-5} mr={1} />
            <VStack
              align="stretch"
              spacing={3}
              mb={2}
              flex={1}
              cursor="pointer"
              onClick={(e) => {
                if (renamingFieldId === field.id) return;
                const rect = e.currentTarget.getBoundingClientRect();
                onFieldContextMenu(field.id, rect.left + 120, rect.top + 20);
              }}
            >
              <HStack spacing={1.5}>
                <Box color="whiteAlpha.400">
                  <Search size={12} strokeWidth={1.5} />
                </Box>
                {renamingFieldId === field.id ? (
                  <Input
                    ref={renameInputRef}
                    value={renameValue}
                    onChange={(e) => onRenameChange(e.target.value)}
                    onBlur={() => onFinishRename(field.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onFinishRename(field.id);
                      if (e.key === 'Escape') onCancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    variant="unstyled"
                    fontSize="sm"
                    fontWeight="600"
                    color="whiteAlpha.500"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    h="auto"
                    p={0}
                  />
                ) : (
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="whiteAlpha.500"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    {field.label}
                  </Text>
                )}
              </HStack>
              {field.loading ? (
                <HStack spacing={2}>
                  <Spinner size="sm" color="rgba(255,255,255,0.4)" speed="0.8s" />
                  <Text fontSize="sm" color="whiteAlpha.400" fontStyle="italic">
                    Researching...
                  </Text>
                </HStack>
              ) : (
                renderFieldValue(field.value)
              )}
            </VStack>
          </HStack>
        </Box>
      ))}

      <Box h="1px" bg="whiteAlpha.50" my={4} />
      <Flex
        align="center"
        cursor="pointer"
        onClick={onAddField}
        color="white"
        _hover={{ color: 'rgba(255,255,255,0.75)' }}
        transition="color 0.15s ease"
        py={2}
        w="fit-content"
        role="group"
      >
        <Flex
          w="24px"
          h="24px"
          borderRadius="6px"
          align="center"
          justify="center"
          border="1px dashed"
          borderColor="currentColor"
          mr={2.5}
        >
          <Plus size={13} strokeWidth={1.5} />
        </Flex>
        <Text fontSize="xs" fontWeight="500">
          {addFieldLabel}
        </Text>
      </Flex>
    </>
  );
}
